import { Pencil, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { formatPhoneNumberIntl } from 'react-phone-number-input/max'

import { extractErrorMessage } from '@/api/extract-error'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList
} from '@/components/ui/combobox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { type AddressPlaceDetails, AddressInput } from '@/lib/address-input'
import {
  type Country,
  isValidPhoneNumber,
  parsePhoneNumber,
  PhoneInput
} from '@/lib/phone-input'

type ContactRow = { id: string } & Record<
  string,
  string | number | null | undefined
>

export type FieldDef<R extends ContactRow> = {
  key: keyof R & string
  label: string
  type?:
    | 'text'
    | 'email'
    | 'tel'
    | 'select'
    | 'number'
    | 'address'
    | 'numberWithUnit'
  required?: boolean
  placeholder?: string
  /** select-only: dropdown options. value is what's stored, label is shown. */
  options?: ReadonlyArray<{ value: string; label: string }>
  /**
   * address-only: which row keys to populate from google place details when
   * the user picks a suggestion. region maps to administrative_area_level_1,
   * country to the canonical english name resolved from the iso code.
   */
  addressAutoFill?: {
    country?: keyof R & string
    region?: keyof R & string
    latitude?: keyof R & string
    longitude?: keyof R & string
  }
  /**
   * numberWithUnit-only: the sibling row key that stores the picked unit, and
   * the pill-toggle options.
   */
  unitKey?: keyof R & string
  unitOptions?: ReadonlyArray<{ value: string; label: string }>
  /** number-only: inclusive bounds enforced on commit. */
  min?: number
  max?: number
}

type ContactRowListProps<R extends ContactRow> = {
  rows: R[]
  fields: ReadonlyArray<FieldDef<R>>
  onAdd: (draft: Omit<R, 'id'>) => Promise<R>
  onSave: (id: string, patch: Partial<Omit<R, 'id'>>) => Promise<void>
  onRemove: (id: string) => Promise<void>
  renderHeader?: (row: R) => React.ReactNode
  addLabel?: string
  emptyDraft: Omit<R, 'id'>
  emptyState?: React.ReactNode
}

type DraftState<R extends ContactRow> =
  | { kind: 'add'; values: Omit<R, 'id'> }
  | { kind: 'edit'; id: string; values: Partial<Omit<R, 'id'>> }
  | null

export function ContactRowList<R extends ContactRow>({
  rows,
  fields,
  onAdd,
  onSave,
  onRemove,
  renderHeader,
  addLabel = 'Add',
  emptyDraft,
  emptyState
}: ContactRowListProps<R>) {
  const [draft, setDraft] = useState<DraftState<R>>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<R | null>(null)

  function startAdd() {
    setError(null)
    setDraft({ kind: 'add', values: { ...emptyDraft } })
  }

  function startEdit(row: R) {
    setError(null)
    setDraft({ kind: 'edit', id: row.id, values: {} })
  }

  function cancel() {
    setDraft(null)
    setError(null)
  }

  function patchDraft(key: keyof R & string, value: string | number | null) {
    patchDraftMany({ [key]: value } as Partial<Omit<R, 'id'>>)
  }

  function patchDraftMany(updates: Partial<Omit<R, 'id'>>) {
    setDraft(prev => {
      if (!prev) return prev
      const nextValues = { ...prev.values, ...updates }
      if (prev.kind === 'add') {
        return { kind: 'add', values: nextValues as Omit<R, 'id'> }
      }
      return {
        kind: 'edit',
        id: prev.id,
        values: nextValues as Partial<Omit<R, 'id'>>
      }
    })
  }

  function validate(values: Partial<R>): string | null {
    for (const field of fields) {
      const raw = values[field.key]
      if (raw === undefined) continue
      const empty = raw === null || String(raw).trim() === ''
      if (field.required && empty) {
        return `${field.label} is required`
      }
      if (empty) continue
      if (field.type === 'email') {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(raw))) {
          return `${field.label} must be a valid email`
        }
      }
      if (field.type === 'tel') {
        if (!isValidPhoneNumber(String(raw))) {
          return `${field.label} must be a valid phone number`
        }
      }
      if (field.type === 'number' && typeof raw === 'number') {
        if (field.min !== undefined && raw < field.min) {
          return `${field.label} must be at least ${field.min}`
        }
        if (field.max !== undefined && raw > field.max) {
          return `${field.label} must be at most ${field.max}`
        }
      }
    }
    return null
  }

  function effectiveDraftValues(): Partial<R> {
    if (!draft) return {} as Partial<R>
    if (draft.kind === 'add') return draft.values as Partial<R>
    const existing = rows.find(row => row.id === draft.id)
    return existing
      ? (mergeRow(existing, draft.values) as Partial<R>)
      : (draft.values as Partial<R>)
  }

  const canSave = draft !== null && validate(effectiveDraftValues()) === null

  async function commit() {
    if (!draft) return
    setError(null)
    if (draft.kind === 'add') {
      const requiredCheck = validate(draft.values as Partial<R>)
      if (requiredCheck) {
        setError(requiredCheck)
        return
      }
      setBusy(true)
      try {
        await onAdd(draft.values)
        setDraft(null)
      } catch (e) {
        setError(extractErrorMessage(e))
      } finally {
        setBusy(false)
      }
    } else {
      const merged = mergeRow(rows.find(r => r.id === draft.id)!, draft.values)
      const requiredCheck = validate(merged as Partial<R>)
      if (requiredCheck) {
        setError(requiredCheck)
        return
      }
      setBusy(true)
      try {
        await onSave(draft.id, draft.values)
        setDraft(null)
      } catch (e) {
        setError(extractErrorMessage(e))
      } finally {
        setBusy(false)
      }
    }
  }

  function requestRemove(row: R) {
    setError(null)
    setPendingDelete(row)
  }

  async function confirmRemove() {
    if (!pendingDelete) return
    const id = pendingDelete.id
    setBusy(true)
    try {
      await onRemove(id)
      if (draft?.kind === 'edit' && draft.id === id) setDraft(null)
      setPendingDelete(null)
    } catch (e) {
      setError(extractErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const addingNew = draft?.kind === 'add'
  const isEmpty = rows.length === 0

  const pendingDeleteLabel = useMemo(() => {
    if (!pendingDelete) return null
    const nameField = fields.find(f => f.key === 'name')
    if (!nameField) return null
    const raw = pendingDelete[nameField.key]
    if (raw === null || raw === undefined || String(raw).trim() === '') {
      return null
    }
    return String(raw)
  }, [pendingDelete, fields])

  return (
    <TooltipProvider delayDuration={150}>
      <div className='flex flex-col gap-2'>
        <ul className='flex flex-col gap-2'>
          {rows.map(row => {
            const editing = draft?.kind === 'edit' && draft.id === row.id
            return (
              <li
                key={row.id}
                className='rounded-xl border border-border bg-page px-4 py-3'
              >
                {renderHeader && (
                  <div className='mb-2'>{renderHeader(row)}</div>
                )}
                {editing ? (
                  <RowEditor
                    fields={fields}
                    row={row}
                    draft={draft.values}
                    onChange={patchDraft}
                    onChangeMany={patchDraftMany}
                    busy={busy}
                    canSave={canSave}
                    onSave={commit}
                    onCancel={cancel}
                  />
                ) : (
                  <RowDisplay
                    fields={fields}
                    row={row}
                    onEdit={() => startEdit(row)}
                    onRemove={() => requestRemove(row)}
                    busy={busy}
                  />
                )}
              </li>
            )
          })}
        </ul>

        {addingNew && (
          <div className='rounded-xl border border-accent bg-accent-light/30 px-4 py-3'>
            <RowEditor
              fields={fields}
              draft={draft.values}
              onChange={patchDraft}
              onChangeMany={patchDraftMany}
              busy={busy}
              canSave={canSave}
              onSave={commit}
              onCancel={cancel}
            />
          </div>
        )}

        {!addingNew && (
          <button
            type='button'
            onClick={startAdd}
            disabled={busy}
            className='flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-cream/20 px-4 py-3 text-[13px] font-medium text-ink-soft transition-colors hover:border-accent hover:bg-accent-light/30 hover:text-accent dark:hover:text-accent-mid disabled:cursor-not-allowed disabled:opacity-50'
          >
            <span className='text-base leading-none'>+</span>
            <span>{isEmpty ? (emptyState ?? addLabel) : addLabel}</span>
          </button>
        )}

        {error && <p className='text-sm text-red-600'>{error}</p>}

        <Dialog
          open={pendingDelete !== null}
          onOpenChange={open => {
            if (busy) return
            if (!open) setPendingDelete(null)
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Remove this contact?</DialogTitle>
              <DialogDescription>
                {pendingDeleteLabel
                  ? `${pendingDeleteLabel} will be removed from your contacts. This can't be undone.`
                  : `This contact will be removed. This can't be undone.`}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <button
                type='button'
                onClick={() => setPendingDelete(null)}
                disabled={busy}
                className='cursor-pointer rounded-md border border-border bg-page px-3 py-1.5 text-[12px] font-medium text-ink-soft hover:bg-cream/40 hover:text-ink disabled:cursor-not-allowed disabled:opacity-50'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={confirmRemove}
                disabled={busy}
                className='cursor-pointer rounded-md bg-destructive px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-[#7f1d1d] disabled:cursor-not-allowed disabled:opacity-50'
              >
                {busy ? 'Removing…' : 'Remove'}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  )
}

function RowDisplay<R extends ContactRow>({
  fields,
  row,
  onEdit,
  onRemove,
  busy
}: {
  fields: ReadonlyArray<FieldDef<R>>
  row: R
  onEdit: () => void
  onRemove: () => void
  busy: boolean
}) {
  return (
    <div className='flex flex-col items-stretch gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3'>
      <div className='flex min-w-0 flex-1 flex-col gap-y-1 sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-x-4'>
        {fields.map(f => {
          const value = row[f.key]
          if (value === null || value === undefined || value === '') return null
          let display: string
          if (f.type === 'select') {
            display =
              f.options?.find(o => o.value === String(value))?.label ??
              String(value)
          } else if (f.type === 'tel') {
            display = formatPhoneNumberIntl(String(value)) || String(value)
          } else if (f.type === 'numberWithUnit') {
            const unitValue = f.unitKey ? row[f.unitKey] : null
            const unitLabel =
              unitValue != null
                ? (f.unitOptions?.find(o => o.value === String(unitValue))
                    ?.label ?? String(unitValue))
                : ''
            display = unitLabel
              ? `${String(value)} ${unitLabel}`
              : String(value)
          } else {
            display = String(value)
          }
          return (
            <div key={String(f.key)} className='min-w-0'>
              <span className='text-[11px] font-medium tracking-wide text-ink-muted uppercase'>
                {f.label}
              </span>{' '}
              <span className='text-[13px] break-words text-ink'>
                {display}
              </span>
            </div>
          )
        })}
      </div>
      {/* touch never shows the tooltip, so below sm the icons wear a label */}
      <div className='flex shrink-0 items-center gap-1 border-t border-border pt-2 sm:border-0 sm:pt-0'>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type='button'
              onClick={onEdit}
              disabled={busy}
              aria-label='Edit'
              className='inline-flex min-h-11 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md text-[12px] font-medium text-ink-muted transition-colors hover:bg-cream/40 hover:text-ink disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0 sm:size-8 sm:flex-none sm:gap-0'
            >
              <Pencil className='size-3.5' />
              <span className='sm:hidden'>Edit</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side='top'>Edit</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type='button'
              onClick={onRemove}
              disabled={busy}
              aria-label='Delete'
              className='inline-flex min-h-11 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md text-[12px] font-medium text-ink-muted transition-colors hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50 sm:size-8 sm:min-h-0 sm:flex-none sm:gap-0 dark:hover:bg-red-950/40 dark:hover:text-red-300'
            >
              <Trash2 className='size-3.5' />
              <span className='sm:hidden'>Delete</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side='top'>Delete</TooltipContent>
        </Tooltip>
      </div>
    </div>
  )
}

const CLEARED_ADDRESS = {
  country: null,
  region: null,
  latitude: null,
  longitude: null
}

type AddressAutoFillKeys = {
  country?: string
  region?: string
  latitude?: string
  longitude?: string
}

function addressAutoFillUpdates(
  keys: AddressAutoFillKeys | undefined,
  values: Pick<
    AddressPlaceDetails,
    'country' | 'region' | 'latitude' | 'longitude'
  >
): Record<string, unknown> {
  const updates: Record<string, unknown> = {}
  if (!keys) return updates
  if (keys.country) updates[keys.country] = values.country
  if (keys.region) updates[keys.region] = values.region
  if (keys.latitude) updates[keys.latitude] = values.latitude
  if (keys.longitude) updates[keys.longitude] = values.longitude
  return updates
}

function RowEditor<R extends ContactRow>({
  fields,
  row,
  draft,
  onChange,
  onChangeMany,
  busy,
  canSave,
  onSave,
  onCancel
}: {
  fields: ReadonlyArray<FieldDef<R>>
  row?: R
  draft: Partial<Omit<R, 'id'>>
  onChange: (key: keyof R & string, value: string | number | null) => void
  onChangeMany: (updates: Partial<Omit<R, 'id'>>) => void
  busy: boolean
  canSave: boolean
  onSave: () => void
  onCancel: () => void
}) {
  function readValue(key: keyof R & string): string {
    const draftRecord = draft as Record<string, unknown>
    if (key in draftRecord) {
      const v = draftRecord[key]
      return v === null || v === undefined ? '' : String(v)
    }
    if (row) {
      const v = row[key]
      return v === null || v === undefined ? '' : String(v)
    }
    return ''
  }

  function readNumber(key: keyof R & string): number | null {
    const text = readValue(key)
    if (text === '') return null
    const parsed = Number(text)
    return Number.isFinite(parsed) ? parsed : null
  }

  function numberBoundsError(field: FieldDef<R>): string | null {
    if (field.type !== 'number') return null
    const value = readNumber(field.key)
    if (value === null) return null
    if (field.min !== undefined && value < field.min) {
      return `${field.label} must be at least ${field.min}`
    }
    if (field.max !== undefined && value > field.max) {
      return `${field.label} must be at most ${field.max}`
    }
    return null
  }

  function addressUnresolved(field: FieldDef<R>): boolean {
    const countryKey = field.addressAutoFill?.country
    if (field.type !== 'address' || !countryKey) return false
    const value = readValue(field.key).trim()
    if (value === '') return false
    const saved = row ? String(row[field.key] ?? '').trim() : ''
    if (value === saved) return false
    return readValue(countryKey).trim() === ''
  }

  const hasUnresolvedAddress = fields.some(addressUnresolved)

  return (
    <div className='flex flex-col gap-3'>
      <div className='grid gap-3 sm:grid-cols-2'>
        {fields.map(f => {
          const id = `field-${String(f.key)}-${row?.id ?? 'draft'}`
          const unresolved = addressUnresolved(f)
          const inlineError = numberBoundsError(f)
          return (
            <div key={String(f.key)} className='flex flex-col gap-1'>
              <Label htmlFor={id} className='text-[11px]'>
                {f.label}
                {f.required && <span className='ml-0.5 text-red-500'>*</span>}
              </Label>
              {f.type === 'select' ? (
                <Combobox
                  items={f.options?.map(o => o.value) ?? []}
                  value={readValue(f.key) || null}
                  onValueChange={v => onChange(f.key, v ?? null)}
                  itemToStringLabel={(item: string) =>
                    f.options?.find(o => o.value === item)?.label ?? item
                  }
                >
                  <ComboboxInput
                    id={id}
                    placeholder={f.placeholder ?? 'Select…'}
                    className='h-11 w-full sm:h-8 sm:w-full'
                  />
                  <ComboboxContent>
                    <ComboboxEmpty>No match.</ComboboxEmpty>
                    <ComboboxList>
                      {(item: string) => (
                        <ComboboxItem key={item} value={item}>
                          {f.options?.find(o => o.value === item)?.label ??
                            item}
                        </ComboboxItem>
                      )}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              ) : f.type === 'tel' ? (
                <PhoneField
                  id={id}
                  placeholder={f.placeholder}
                  value={readValue(f.key)}
                  onChange={v => onChange(f.key, v === '' ? null : v)}
                />
              ) : f.type === 'number' ? (
                <Input
                  id={id}
                  type='text'
                  inputMode='numeric'
                  pattern='[0-9]*'
                  placeholder={f.placeholder}
                  value={readValue(f.key)}
                  onChange={e => {
                    const digits = e.target.value.replace(/\D/g, '')
                    onChange(f.key, digits === '' ? null : Number(digits))
                  }}
                  className='h-11 sm:h-8'
                />
              ) : f.type === 'numberWithUnit' ? (
                <div className='flex items-center gap-2'>
                  <Input
                    id={id}
                    type='text'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    placeholder={f.placeholder}
                    value={readValue(f.key)}
                    onChange={e => {
                      const digits = e.target.value.replace(/\D/g, '')
                      onChange(f.key, digits === '' ? null : Number(digits))
                    }}
                    className='h-11 flex-1 sm:h-8'
                  />
                  <div
                    role='group'
                    aria-label={`${f.label} unit`}
                    className='flex items-center gap-1'
                  >
                    {f.unitOptions?.map(opt => {
                      const selected =
                        f.unitKey !== undefined &&
                        readValue(f.unitKey) === opt.value
                      return (
                        <button
                          key={opt.value}
                          type='button'
                          onClick={() =>
                            f.unitKey && onChange(f.unitKey, opt.value)
                          }
                          aria-pressed={selected}
                          className={`cursor-pointer rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                            selected
                              ? 'border-accent bg-accent text-white'
                              : 'border-border bg-page text-ink hover:border-accent/40 hover:bg-cream/40'
                          }`}
                        >
                          {opt.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ) : f.type === 'address' ? (
                <AddressInput
                  id={id}
                  value={readValue(f.key)}
                  placeholder={f.placeholder}
                  onChange={value =>
                    onChange(f.key, value === '' ? null : value)
                  }
                  onInvalidate={() => {
                    // fires per keystroke, so skip the dispatch once cleared
                    const cleared = addressAutoFillUpdates(
                      f.addressAutoFill,
                      CLEARED_ADDRESS
                    )
                    const hasStaleGeo = Object.keys(cleared).some(
                      key => readValue(key as keyof R & string) !== ''
                    )
                    if (hasStaleGeo) {
                      onChangeMany(cleared as Partial<Omit<R, 'id'>>)
                    }
                  }}
                  onSelect={details =>
                    onChangeMany({
                      [f.key]: details.description,
                      ...addressAutoFillUpdates(f.addressAutoFill, details)
                    } as Partial<Omit<R, 'id'>>)
                  }
                  error={
                    unresolved
                      ? 'Select your address from the suggestions so we can record your country.'
                      : undefined
                  }
                />
              ) : (
                <Input
                  id={id}
                  type={f.type ?? 'text'}
                  inputMode={f.type === 'email' ? 'email' : undefined}
                  placeholder={f.placeholder}
                  value={readValue(f.key)}
                  onChange={e => {
                    const v = e.target.value
                    onChange(f.key, v === '' && !f.required ? null : v)
                  }}
                  className='h-11 sm:h-8'
                />
              )}
              {inlineError && (
                <span className='text-[11px] text-red-600'>{inlineError}</span>
              )}
            </div>
          )
        })}
      </div>
      <div className='flex items-center justify-end gap-2'>
        <button
          type='button'
          onClick={onCancel}
          disabled={busy}
          className='cursor-pointer rounded-md border border-border bg-page px-3 py-1.5 text-[12px] font-medium text-ink-soft hover:bg-cream/40 hover:text-ink disabled:cursor-not-allowed disabled:opacity-50'
        >
          Cancel
        </button>
        <button
          type='button'
          onClick={onSave}
          disabled={busy || !canSave || hasUnresolvedAddress}
          className='cursor-pointer rounded-md bg-accent px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-[#15296b] disabled:cursor-not-allowed disabled:opacity-50'
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}

/**
 * country is held locally so emptying the input doesn't revert to the
 * default country. seed from the stored E.164 value (if any) so an existing
 * UK number shows as UK on first render.
 */
function PhoneField({
  id,
  value,
  placeholder,
  onChange
}: {
  id: string
  value: string
  placeholder?: string
  onChange: (next: string) => void
}) {
  const [country, setCountry] = useState<Country>(() => {
    if (value) {
      try {
        const parsed = parsePhoneNumber(value)
        if (parsed?.country) return parsed.country
      } catch {
        // fall through to default
      }
    }
    return 'US'
  })

  return (
    <PhoneInput
      id={id}
      defaultCountry={country}
      onCountryChange={c => {
        if (c) setCountry(c)
      }}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className='h-11 sm:h-8'
    />
  )
}

function mergeRow<R extends ContactRow>(
  row: R,
  patch: Partial<Omit<R, 'id'>>
): R {
  return { ...row, ...patch }
}
